import React from 'react';
import LegalPage from './LegalPage';

const Privacy: React.FC = () => (
  <LegalPage title="Privacy Policy" updated="August 22, 2026">
    <p>
      This policy explains what Clevor collects, why, who else sees it, and what you can do about
      it. It describes the service as it actually operates today — the third-party services listed
      below are the ones the software genuinely calls, not a generic list.
    </p>

    <h2>What we collect</h2>
    <h3>You give us</h3>
    <ul>
      <li>Account details: name, email address, password (stored hashed, never in plain text), and optionally a phone number.</li>
      <li>Company profile: company name, size, industry, website, address, NAICS codes, capabilities, set-aside certifications, CAGE code, UEI (SAM.gov identifier), and an optional logo.</li>
      <li>Work product: opportunities you save, pipeline notes, past-performance records, pricing information, checklist answers, and the proposal content you or Clevor generate.</li>
      <li>Documents: solicitation files retrieved on your behalf, and the text extracted from them.</li>
    </ul>

    <h3>We generate</h3>
    <ul>
      <li>Match scores, compliance findings, vendor shortlists, and generated proposal sections.</li>
      <li>Usage records: which AI actions you ran, when, how long they took, and AI credits consumed.</li>
      <li>Security and audit records: sign-in times, IP address, browser user agent, and a log of significant account actions.</li>
    </ul>

    <h2>Why we collect it</h2>
    <p>
      To run the service you asked for: matching opportunities to your business, analysing
      solicitations, checking eligibility, finding vendors, drafting proposals, and keeping your
      account secure. We do not sell your data, and we do not use it for advertising.
    </p>

    <h2>Who else receives it</h2>
    <p>
      Clevor cannot deliver its core features without sending some of your data to other services.
      These are all of them:
    </p>
    <ul>
      <li>
        <strong>AI processing.</strong> To generate analyses and proposal sections we send
        solicitation text and relevant parts of your company profile to AI providers, routed
        through OpenRouter and through Clevor's own hosted analysis service. Our OpenRouter
        account is configured so that requests are not routed to endpoints that train on request
        data. Providers may retain request data briefly for abuse monitoring and operational
        purposes under their own policies; retention is governed by the provider serving the
        request, and we do not control it. We consider this the most sensitive data flow in the
        product and we describe it in detail rather than burying it.
      </li>
      <li><strong>SAM.gov (U.S. General Services Administration).</strong> We query public federal opportunity and entity records. We send search criteria and identifiers, not your account data.</li>
      <li><strong>Stripe.</strong> Payment processing. Card details go directly to Stripe and are never stored on Clevor's servers. Billing is currently disabled during the private beta.</li>
      <li><strong>Resend</strong> and our mail server. Transactional email: verification codes, password resets, and notifications.</li>
      <li><strong>Zippopotam.us.</strong> Converts ZIP codes to coordinates so vendor results can be sorted by distance. It receives ZIP codes only — no personal or company data.</li>
    </ul>
    <p>
      We may also disclose information if required by law, or to protect the rights and safety of
      users and the service.
    </p>

    <h2>How long we keep it</h2>
    <p>
      Account and work-product data is retained while your account is active. Operational logs are
      retained on a rolling basis for security and troubleshooting. Deleting your account deletes
      your data as described below.
    </p>

    <h2>Your choices</h2>
    <ul>
      <li><strong>Export.</strong> You can download the data associated with your account from your account settings.</li>
      <li><strong>Deletion.</strong> You can delete your account. This is a real deletion — your user record and the data linked to it are removed from our database, not flagged as hidden. A minimal audit record that an account was deleted is retained. Data already sent to AI providers or to Stripe is governed by their retention policies and may persist there.</li>
      <li><strong>Correction.</strong> You can edit your profile and company information at any time.</li>
      <li><strong>Email preferences.</strong> Notification settings are in your account. Security and transactional emails cannot be turned off while your account is open.</li>
    </ul>

    <h2>Security</h2>
    <p>
      Passwords are hashed. Sessions use expiring tokens. Sensitive stored credentials are
      encrypted at rest. Two-factor authentication is available, and is required on Pro and
      Enterprise plans. No system is perfectly secure, and we do not claim otherwise.
    </p>

    <h2>Your responsibilities</h2>
    <p>
      Clevor is built for federal contracting, so you may upload material that is sensitive to your
      business. Please do not upload classified information, controlled unclassified information
      requiring safeguards we have not agreed to in writing, or personal information about others
      that you are not entitled to share. Clevor is not currently offered as a FedRAMP-authorized
      or CMMC-certified service.
    </p>

    <h2>Children</h2>
    <p>Clevor is a business tool and is not directed to anyone under 18.</p>

    <h2>Where data is processed</h2>
    <p>
      Clevor operates in the United States, and AI providers may process requests in other
      countries. If you use Clevor from outside the U.S., you consent to processing in the U.S.
    </p>

    <h2>Changes</h2>
    <p>
      We will update this policy as the service changes and will revise the date above. Material
      changes will be communicated to account holders by email.
    </p>

    <h2>Contact</h2>
    <p>
      <a href="mailto:support@clevor.ai">support@clevor.ai</a>
    </p>
  </LegalPage>
);

export default Privacy;
